import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, Clock, Users, Target, Wrench, AlertTriangle, CheckCircle2, ClipboardList, Search, X, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { bcGuides, bcGuideGet } from "@/lib/api";

export function BusinessCenterGuides() {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ categories: [], difficulties: [], industries: [], tags: [] });
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const cat = params.get("category") || "";
  const diff = params.get("difficulty") || "";
  const industry = params.get("industry") || "";
  const tag = params.get("tag") || "";

  useEffect(() => {
    document.title = "How-To Guides | WebsiteTech.pro";
    bcGuides({
      q: q || undefined,
      category: cat || undefined,
      difficulty: diff || undefined,
      industry: industry || undefined,
      tag: tag || undefined,
    }).then((d) => { setItems(d.items || []); setFacets(d.facets || facets); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, cat, diff, industry, tag]);

  const setFacet = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const activeFilters = [
    cat && ["category", cat], diff && ["difficulty", diff],
    industry && ["industry", industry], tag && ["tag", tag],
  ].filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-guides">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Business Center</Link>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900">How-to guides</h1>
          <p className="mt-2 text-slate-600 max-w-3xl">Actionable playbooks — every guide lists prerequisites, step-by-step instructions, templates, success metrics and the tools that back it up.</p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Sidebar facets */}
            <aside className="md:col-span-1 space-y-6">
              <div className="rounded-xl bg-white border border-slate-200 p-4">
                <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">Search</div>
                <div className="mt-2 relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                  <input value={q} onChange={(e) => setQ(e.target.value)}
                    data-testid="bc-guides-search"
                    placeholder="Search guides…"
                    className="w-full pl-7 pr-2 py-2 rounded-lg border border-slate-300 text-sm" />
                </div>
              </div>
              <FacetGroup label="Category" facets={facets.categories} active={cat} onPick={(v) => setFacet("category", v === cat ? "" : v)} testId="bc-guides-cat" />
              <FacetGroup label="Difficulty" facets={facets.difficulties} active={diff} onPick={(v) => setFacet("difficulty", v === diff ? "" : v)} testId="bc-guides-diff" />
              <FacetGroup label="Industry" facets={facets.industries} active={industry} onPick={(v) => setFacet("industry", v === industry ? "" : v)} testId="bc-guides-ind" />
              <FacetGroup label="Tags" facets={facets.tags} active={tag} onPick={(v) => setFacet("tag", v === tag ? "" : v)} testId="bc-guides-tag" />
            </aside>

            {/* Grid */}
            <div className="md:col-span-3">
              {activeFilters.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-slate-500">Filters:</span>
                  {activeFilters.map(([k, v]) => (
                    <button key={k} onClick={() => setFacet(k, "")}
                      className="text-xs bg-indigo-100 text-indigo-800 rounded-full px-2 py-0.5 inline-flex items-center gap-1 hover:bg-indigo-200"
                      data-testid={`bc-guides-clear-${k}`}>
                      {k}: {v} <X className="w-3 h-3" />
                    </button>
                  ))}
                </div>
              )}
              <div className="text-xs text-slate-500 mb-3">{items.length} guides</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {items.map((g) => (
                  <Link key={g.slug} to={`/business-center/guides/${g.slug}`} data-testid={`bc-guide-${g.slug}`}
                        className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-indigo-400 hover:shadow-md transition">
                    <div className="flex items-start justify-between gap-3">
                      <BookOpen className="w-5 h-5 text-indigo-600" />
                      <span className={`text-[10px] uppercase tracking-widest font-bold rounded px-2 py-0.5 ${
                        g.difficulty === "beginner" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                        g.difficulty === "intermediate" ? "bg-amber-50 text-amber-800 border border-amber-200" :
                        "bg-rose-50 text-rose-700 border border-rose-200"
                      }`}>{g.difficulty}</span>
                    </div>
                    <div className="mt-3 font-bold text-slate-900 group-hover:text-indigo-700 leading-snug">{g.title}</div>
                    <div className="mt-2 text-sm text-slate-600 line-clamp-2">{g.objective}</div>
                    <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {g.time_to_implement || "—"}</span>
                      <span>· {g.step_count} steps</span>
                      <span>· {g.category}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {(g.tags || []).slice(0, 3).map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">#{t}</span>
                      ))}
                    </div>
                  </Link>
                ))}
                {items.length === 0 && (
                  <div className="col-span-full text-center text-slate-500 text-sm py-16 border border-dashed rounded-2xl">
                    No guides match your filters — try clearing them.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function FacetGroup({ label, facets, active, onPick, testId }) {
  if (!facets || facets.length === 0) return null;
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4">
      <div className="text-xs uppercase font-bold text-slate-500 tracking-widest">{label}</div>
      <div className="mt-2 space-y-1">
        {facets.map((f) => (
          <button key={f.key} onClick={() => onPick(f.key)}
            data-testid={`${testId}-${f.key}`}
            className={`w-full text-left text-xs rounded px-2 py-1 flex items-center justify-between transition ${
              active === f.key ? "bg-indigo-600 text-white" : "hover:bg-slate-100 text-slate-700"
            }`}>
            <span className="capitalize">{f.key}</span>
            <span className={active === f.key ? "text-indigo-200" : "text-slate-400"}>{f.count}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function BusinessCenterGuideDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [checks, setChecks] = useState({});
  useEffect(() => {
    bcGuideGet(slug).then(setData).catch(() => setData({ guide: null, related: [] }));
    try {
      const saved = JSON.parse(localStorage.getItem(`guide_progress_${slug}`) || "{}");
      setChecks(saved);
    } catch {}
  }, [slug]);
  useEffect(() => {
    localStorage.setItem(`guide_progress_${slug}`, JSON.stringify(checks));
  }, [slug, checks]);

  if (!data) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="pt-32 text-center text-slate-500">Loading…</div></div>;
  const g = data.guide;
  if (!g) return <div className="min-h-screen bg-slate-50"><Navbar /><div className="pt-32 text-center text-slate-500">Guide not found.</div></div>;

  const totalSteps = (g.steps || []).length;
  const doneCount = (g.steps || []).filter((_, i) => checks[i]).length;
  const progress = totalSteps ? Math.round((doneCount / totalSteps) * 100) : 0;

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      <main className="flex-1 pt-24 pb-16" data-testid="bc-guide-detail">
        <div className="max-w-4xl mx-auto px-4">
          <div className="mb-3 text-xs uppercase tracking-widest text-indigo-700 font-bold">
            <Link to="/business-center/guides" className="hover:text-indigo-900 inline-flex items-center gap-1"><ArrowLeft size={12} /> Guides</Link>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="uppercase tracking-widest font-bold text-slate-600">{g.category}</span>
            <span className={`uppercase tracking-widest font-bold rounded px-2 py-0.5 ${
              g.difficulty === "beginner" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
              g.difficulty === "intermediate" ? "bg-amber-50 text-amber-800 border border-amber-200" :
              "bg-rose-50 text-rose-700 border border-rose-200"
            }`}>{g.difficulty}</span>
            <span className="text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {g.time_to_implement}</span>
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900">{g.title}</h1>
          <p className="mt-3 text-lg text-slate-700">{g.objective}</p>
          <p className="mt-2 text-slate-600 text-sm inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {g.audience}</p>

          {/* Progress */}
          <div className="mt-6 rounded-xl bg-white border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Progress</div>
            <div className="mt-2 flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${progress}%` }} data-testid="bc-guide-progress-bar" />
              </div>
              <div className="text-sm font-bold text-slate-800">{doneCount} / {totalSteps}</div>
            </div>
          </div>

          <Sect icon={ClipboardList} title="Prerequisites"><Bullets items={g.prerequisites} /></Sect>

          <Sect icon={Target} title="Step-by-step">
            <ol className="space-y-3">
              {(g.steps || []).map((s, i) => (
                <li key={i} className={`rounded-xl border p-4 transition ${checks[i] ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`} data-testid={`bc-guide-step-${i}`}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={!!checks[i]}
                      onChange={(e) => setChecks((c) => ({ ...c, [i]: e.target.checked }))}
                      data-testid={`bc-guide-step-check-${i}`}
                      className="mt-1 w-4 h-4 accent-emerald-600" />
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs font-bold text-indigo-700">Step {i + 1}</span>
                        <span className="font-semibold text-slate-900">{s.title}</span>
                      </div>
                      <div className="mt-1 text-sm text-slate-700">{s.detail}</div>
                      {s.success_check && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Success check: {s.success_check}
                        </div>
                      )}
                    </div>
                  </label>
                </li>
              ))}
            </ol>
          </Sect>

          {(g.templates || []).length > 0 && (
            <Sect icon={Sparkles} title="Templates" tone="indigo">
              <div className="space-y-2">
                {g.templates.map((t, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`bc-guide-template-${i}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-semibold text-slate-900 text-sm">{t.label}</div>
                      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t.kind}</span>
                    </div>
                    {t.subject && <div className="mt-1 text-xs text-slate-600"><strong>Subject:</strong> {t.subject}</div>}
                    <pre className="mt-2 text-xs text-slate-700 whitespace-pre-wrap bg-slate-50 rounded p-2 border">{t.body}</pre>
                  </div>
                ))}
              </div>
            </Sect>
          )}

          <Sect icon={Target} title="Success metrics" tone="emerald"><Bullets items={g.success_metrics} /></Sect>

          {(g.related_tools || []).length > 0 && (
            <Sect icon={Wrench} title="Recommended tools">
              <div className="flex flex-wrap gap-2">
                {g.related_tools.map((s) => (
                  <Link key={s} to={`/business-center/tools`} className="text-xs px-2 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-800 font-semibold">{s}</Link>
                ))}
              </div>
            </Sect>
          )}

          {(g.related_playbooks || []).length > 0 && (
            <Sect icon={BookOpen} title="Related playbooks">
              <div className="flex flex-wrap gap-2">
                {g.related_playbooks.map((s) => (
                  <Link key={s} to={`/business-center/playbooks/${s}`} className="text-xs px-2 py-1 rounded-full bg-slate-100 border border-slate-200 text-slate-800 font-semibold hover:bg-slate-200">{s}</Link>
                ))}
              </div>
            </Sect>
          )}

          <Sect icon={AlertTriangle} title="Common mistakes" tone="rose"><Bullets items={g.common_mistakes} /></Sect>

          {(data.related || []).length > 0 && (
            <section className="mt-10">
              <div className="text-sm font-bold uppercase tracking-widest text-slate-700">Continue learning</div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {data.related.map((r) => (
                  <Link key={r.slug} to={`/business-center/guides/${r.slug}`}
                    className="rounded-xl border border-slate-200 bg-white p-4 hover:border-indigo-400 transition">
                    <div className="text-xs uppercase tracking-widest text-slate-500">{r.category}</div>
                    <div className="mt-1 font-semibold text-slate-900">{r.title}</div>
                    <div className="mt-2 text-xs text-slate-500 uppercase">{r.difficulty}</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="mt-10 rounded-3xl bg-slate-950 text-white p-8">
            <h2 className="text-xl md:text-2xl font-bold">Want us to run this guide for you?</h2>
            <p className="mt-2 text-slate-300 text-sm">Skip the setup — we'll implement it, brand it, and measure it. Every deliverable is human-approved.</p>
            <Link to="/contact" className="mt-4 inline-flex items-center gap-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 px-5 py-3 font-semibold transition">Request implementation <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Sect({ icon: Icon, title, tone, children }) {
  const tones = { indigo: "text-indigo-700", rose: "text-rose-700", emerald: "text-emerald-700" };
  return (
    <section className="mt-10">
      <div className={`text-sm font-bold uppercase tracking-widest flex items-center gap-2 ${tones[tone] || "text-slate-700"}`}>
        <Icon className="w-4 h-4" /> {title}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}
function Bullets({ items }) {
  return (
    <ul className="space-y-1.5">
      {(items || []).map((it, i) => (<li key={i} className="text-sm text-slate-800 flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-indigo-500 flex-shrink-0" />{it}</li>))}
    </ul>
  );
}
